/**
 * `/agents/workflows` and `/agents/workflows/new` — RO-3-02b + RO-3-02c (#44399).
 *
 * Port of `excellent/app/agents/workflows/page.tsx` (36 LOC of wrapper over 428
 * LOC of delegates: `_fleet-console` + `_compiled-workflows` + `_gates-panel`
 * + `_gates-panel-client`) and `.../new/page.tsx` (43 over 360:
 * `_builder` + `_wizard`).
 *
 * The three sections of the fleet page are three DIFFERENT primitives, which is
 * why they are three sections and not one table — a `loop` is an optimizable
 * prompt pipeline, a compiled workflow is an agentic decision mined into
 * rule-data, and a gate is a shell command a project's owner declared. That
 * distinction is the page's whole IA and it is preserved verbatim.
 *
 * RO-3-02b deferred `<GatesPanel>` because the gates contract was not yet
 * mounted on Hono. That mount landed on `agents/integration` (`tail.ts`:
 * `gates.list` + `gates.run`); RO-3-02c closes the section with a pure client
 * port against those contracts (see `../shared/agents/gates-panel.tsx`).
 */

import * as React from "react";
import { z } from "zod";
import { createRoute } from "@tanstack/react-router";
import {
  Card,
  IntentBadge,
  PageHeader,
  SectionTitle,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  compilations,
  type CompilationListItem,
} from "@excellent/ui/api/contracts/compilations";
import type { WorkflowRange } from "@/optimization/db-ranges";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";
import { NewWorkflowBuilders } from "./workflows/new-builders";
import { FleetConsole } from "../shared/agents/fleet-console";
import { GatesPanel } from "../shared/agents/gates-panel";

// ── /agents/workflows — the fleet console ────────────────────────────────────

const fleetSearchSchema = routeSearchSchema({
  /**
   * Report window for the fleet's runs/cost/tokens columns. Route-local: the
   * studio's own `range` (`1d|7d|30d|all`) is a DIFFERENT vocabulary on a
   * different route, which is exactly why neither is shell-wide. Declaring it
   * here is what makes the two impossible to confuse.
   */
  range: z.enum(["all", "30d", "7d", "24h"]).optional(),
});

function WorkflowsFleet() {
  const search = agentsWorkflowsRoute.useSearch();
  const range: WorkflowRange = search.range ?? "all";

  return (
    <div className="flex flex-col">
      <div className="@container/page flex flex-col gap-10 px-6 py-8">
        <FleetConsole range={range} />
        <CompiledWorkflows />
        <GatesPanel />
      </div>
    </div>
  );
}

const STATUS_INTENT: Record<
  CompilationListItem["status"],
  "info" | "success" | "warning" | "danger" | "neutral"
> = {
  compiled: "success",
  compiling: "info",
  sourcing: "info",
  defined: "neutral",
  paused: "warning",
};

function pct(x: number): string {
  return `${(x * 100).toFixed(0)}%`;
}

/**
 * Compiled workflows — recurring agentic decisions mined into rule-data. The
 * `compilations.list` contract and its Hono mount already existed (RO-1-10), so
 * this section needed no new endpoint: the only change from the original is that
 * the list arrives through `useApi` instead of a server-side `listCompilations`.
 */
function CompiledWorkflows() {
  const { data } = useApi(compilations.list, {});
  const items = data?.items ?? [];

  return (
    <section className="flex flex-col gap-4" data-section-compiled="agents">
      <SectionTitle
        variant="plain"
        size="lg"
        title="Compiled workflows"
        subtitle="Recurring agentic decisions mined into deterministic rule-data — cheaper and more reliable the more they run. The human only adjudicates genuine splits."
      />
      <div className="grid grid-cols-1 gap-3 @2xl/page:grid-cols-2">
        {items.map((item) => (
          <Card interactive asChild key={item.slug}>
            <Link
              href={`/agents/workflows/compiled/${item.slug}`}
              className="flex flex-col gap-2 p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="type-body font-medium text-foreground">{item.title}</span>
                <div className="flex shrink-0 items-center gap-1.5">
                  {item.unresolvedDisputes > 0 && (
                    <IntentBadge intent="info">{item.unresolvedDisputes} to refine</IntentBadge>
                  )}
                  <IntentBadge intent={STATUS_INTENT[item.status]}>{item.status}</IntentBadge>
                </div>
              </div>
              <p className="line-clamp-2 type-subtext text-muted-foreground">{item.charter}</p>
              {item.routing.scored ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 type-subtext tabular-nums text-muted-foreground">
                  <span className="text-foreground/80">{item.ruleCounts.accepted} rules</span>
                  <span aria-hidden>·</span>
                  <span>auto {pct(item.routing.auto)}</span>
                  <span>LLM {pct(item.routing.tier2)}</span>
                  <span>human {pct(item.routing.human)}</span>
                  <span aria-hidden>·</span>
                  <span>holdout {pct(item.routing.holdoutAccuracy)}</span>
                </div>
              ) : (
                <p className="type-subtext text-muted-foreground">
                  Not compiled yet — run{" "}
                  <code className="rounded-xs bg-muted px-1 py-0.5 font-mono text-foreground/80">
                    {item.sourceCommand}
                  </code>
                </p>
              )}
            </Link>
          </Card>
        ))}
      </div>
    </section>
  );
}

export const agentsWorkflowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/workflows",
  validateSearch: fleetSearchSchema,
  component: WorkflowsFleet,
});

// ── /agents/workflows/new — the two builders ─────────────────────────────────

const newWorkflowSearchSchema = routeSearchSchema({
  /** `?mode=form` swaps the guided builder for the quick form, as before. */
  mode: z.enum(["form"]).optional(),
});

function NewWorkflow() {
  const search = agentsWorkflowsNewRoute.useSearch();
  const useForm = search.mode === "form";

  return (
    <div className="flex flex-col">
      <div className="px-6 py-8">
        <PageHeader
          className="mb-5"
          title="New workflow"
          description={
            useForm
              ? "Define an optimizable workflow + its objective directly. Lands in draft (off) until you activate it."
              : "Describe what you want; the builder proposes a pipeline you confirm step by step."
          }
          toolbar={
            <Link
              href={useForm ? "/agents/workflows/new" : "/agents/workflows/new?mode=form"}
              className="shrink-0 type-body text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              {useForm ? "← Guided builder" : "Use the quick form →"}
            </Link>
          }
        />
        <div className="max-w-2xl">
          <NewWorkflowBuilders mode={useForm ? "form" : "guided"} />
        </div>
      </div>
    </div>
  );
}

export const agentsWorkflowsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/workflows/new",
  validateSearch: newWorkflowSearchSchema,
  component: NewWorkflow,
});
